import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import DefaultApiLimit from '../../../../../config/defaultLimit';
import currentTimeZone from '../../../../../config/timeZone';
import {
  RequestLocationActivityInterface,
  LocationActivityInterface,
} from '../../../../../interface/location-activity-interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import {
  disableLocationActivityFiltersApplied,
  fetchLocationActivity,
  fetchLocationActivityFilters,
  updateLocationActivityFilters,
  setSortData,
} from '../../../../../redux/slices/LocationActivitySlice';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import CustomDateRangePicker, {
  calculateDateRange,
  normalizePresetKey,
} from '../../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../../shared-components/custom-table/CustomTable';
import OcppLogSidePanel from '../../../../../shared-components/table-side-panel/OcppLogsSidePanel';
import { GetParametersFromCommonFilters } from '../../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../../../shared-functions/ExportFile';
import FormatUtcString from '../../../../../shared-functions/FormatUtcString';
import stationManagementApi from '../../../../../utils/api/location-management';

/**
 * Station activity component
 * @returns {any} The rendered component
 */
function LocationActivity() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();

  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() => {
    const defaultRange = calculateDateRange(normalizePresetKey('last14Days'), 'future');
    return {
      startDate: defaultRange?.startDate || '',
      endDate: defaultRange?.endDate || '',
    };
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Get query parameters for the API call
   * @param {any[]} activeFilters - The active filters
   * @returns {any} The query parameters
   */
  const getQueryParameters = (activeFilters: any[]) => {
    const queryParameters = GetParametersFromCommonFilters(activeFilters);
    if (dateRange.startDate) {
      queryParameters.startDate = dateRange.startDate;
      queryParameters.timeZone = currentTimeZone;
    }
    if (dateRange.endDate) {
      queryParameters.endDate = dateRange.endDate;
      if (!queryParameters.timeZone) queryParameters.timeZone = currentTimeZone;
    }
    return queryParameters;
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          requiredTime
        />
      </div>
    );
  };
  const {
    stationsActivity,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.locationActivity);
  const { selectedChargingPoint } = useAppSelector((state) => state.locationManagement);
  const lastMins = false;

  const column = useMemo(
    () => [
      {
        label: t('date'),
        accessorKey: 'created_at' as keyof LocationActivityInterface,
        sortAllow: true,

        /**
         * Converting timestamp to UTC date
         * @param {any} stationActivity - The activity object
         * @returns {any} The rendered element
         */
        customData: (stationActivity: LocationActivityInterface) => (
          <p>{ConvertUTCtoDate(stationActivity.created_at)}</p>
        ),
      },
      {
        label: t('time'),
        accessorKey: 'time' as keyof LocationActivityInterface,
        sortAllow: true,
        /**
         * Converting timestamp to UTC time
         * @param {any} stationActivityTime - The activity object
         * @returns {any} The rendered element
         */
        customData: (stationActivityTime: LocationActivityInterface) => (
          <p>{FormatUtcString(ConvertUTCtoHHMM(stationActivityTime.created_at))}</p>
        ),
      },
      {
        label: t('type'),
        accessorKey: 'type' as keyof LocationActivityInterface,
        sortAllow: false,

        /**
         * Converting timestamp to UTC time
         * @param {any} stationActivity - The activity object
         * @returns {any} The rendered element
         */
        customData: (stationActivity: LocationActivityInterface) => {
          const type = stationActivity.type?.toLowerCase();

          return (
            <p className={`${type === 'in' ? 'type-in' : 'type-out'}`}>
              {type.charAt(0).toUpperCase()}
              {type.slice(1)}
            </p>
          );
        },
      },
      {
        label: t('messageType'),
        accessorKey: 'action' as keyof LocationActivityInterface,
      },
      {
        label: t('message'),
        accessorKey: 'message' as keyof LocationActivityInterface,
        columnWidth: 500,
      },
    ],
    [t],
  );

  /**
   * Handle the fetch the data in api function
   * @param {any} data The fetch params
   */
  const fetchData = (data: RequestLocationActivityInterface) => {
    if (!loading && selectedChargingPoint) {
      dispatch(
        fetchLocationActivity({
          ...data,
          chargingStation: selectedChargingPoint?.id,
        }),
      );
    }
  };
  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'created_at' },
    { label: t('time'), accessorKey: 'time' },
    { label: t('type'), accessorKey: 'type' },
    { label: t('actionType'), accessorKey: 'action' },
    { label: t('message'), accessorKey: 'message' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function for pdf and csv
   * @param {string} type pdf or csv
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = getQueryParameters(filters);
    setExportLoader(true);
    stationManagementApi
      .fetchLocationActivityExport({
        ...queryParameters,
        chargingStation: selectedChargingPoint?.id,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: LocationActivityInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'created_at':
                return ConvertUTCtoDate(row?.created_at) || 'N/A';
              case 'time':
                return ConvertUTCtoHHMM(row?.created_at) || 'N/A';
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'Station Activity.csv');
        } else {
          exportPDF(headers, rows, 'Station Activity.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };
  /**
   * Table pagination function
   * @param {any} value The page props
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = getQueryParameters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = getQueryParameters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      last15min: lastMins,
      sortBy,
      sortField,
    });
  };

  /**
   * Function to handleAutoRefresh
   */
  const handleAutoRefresh = () => {
    const queryParameters = getQueryParameters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      last15min: lastMins,
      sortBy,
      sortField,
    });
  };
  /**
   * Function for sorting table data
   * @param {object} sortData The sort configuration
   * @param {any} sortData.sortBy - The sortBy prop
   * @param {any} sortData.sortField - The sortField prop
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = getQueryParameters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
      last15min: lastMins,
    });
  };

  /**
   * Function to add search params, side panel will open automatically
   * @param {string} stationId The station id
   */
  const handleOpenSidePanelOnRowClick = (stationId: string) => {
    // Set or update the logId param
    searchParameters.set('stationId', stationId);

    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);
  };

  useEffect(() => {
    if (
      isFilterUpdated ||
      stationsActivity.length === 0 ||
      dateRange.startDate ||
      dateRange.endDate ||
      selectedChargingPoint?.id
    ) {
      const queryParameters = getQueryParameters(filters);
      fetchData({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit,
        last15min: lastMins,
        sortBy,
        sortField,
      });

      dispatch(disableLocationActivityFiltersApplied());
    }
  }, [filters, dateRange.startDate, dateRange.endDate, selectedChargingPoint?.id]);

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('LocationActivityFilters');

    // If same id as last time -> do nothing
    if (lastId !== currentId || filters.length === 1) {
      dispatch(fetchLocationActivityFilters({ chargingStation: currentId }));
    }
    if (stationsActivity.length === 0 || lastId !== currentId) {
      const queryParameters = getQueryParameters(filters);
      fetchData({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit,
        last15min: lastMins,
        sortBy,
        sortField,
      });

      dispatch(disableLocationActivityFiltersApplied());
    }
    // Save this id so next time we can compare
    sessionStorage.setItem('LocationActivityFilters', currentId);
  }, [selectedChargingPoint?.id, filters]);

  return (
    <div className="location-activity-wrap" dir={i18n.dir()}>
      <div className="location-activity-table">
        <CustomTable
          version="v2"
          header={column}
          data={stationsActivity}
          serialNo
          sizeChangeButtonRequired
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          filterListLoading={filterListLoading}
          filters={filters}
          filtersToDispatch={updateLocationActivityFilters}
          filterListToDispatch={fetchLocationActivityFilters}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          refershCurrentPages={handleRefresh}
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          pageLimitChange={pageNateLimitChange}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey="stationId"
          rowClickSidePanel={<OcppLogSidePanel panelType="station" fetchIdFrom="queryParams" />}
          height={400}
          onHiddenColumnsChange={setHiddenColumns}
          rightTopContent={renderDateFilters()}
          onClearFilterChange={() => {
            clearDateRange();
          }}
        />
      </div>
    </div>
  );
}

export default LocationActivity;
